import React, {
  useState, useEffect, useMemo, useCallback, useRef
} from 'react';
import PropTypes from 'prop-types';
import { message, notification as NotifcationUI, Modal } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import "moment-timezone";
import { Scrollbars } from 'react-custom-scrollbars';
import {
  ChevronLeft, ChevronRight, Search, X, CalendarDays, Check, Ban, Loader2,
} from 'lucide-react';
import { CalendarIcon } from '../../Icons/Icons';
import { getSocket } from '../../../Api/Socket';
import { caldrSuccess } from '../../../redux/CrmReduxCalls/CalendarResponse';
import 'react-calendar/dist/Calendar.css';
import Popup from '../../../container/dashboard/Popup/CalendarMeetingpopup';
import { FetchAllRecentMeetings } from '../../../Api/Services/MeetingApiService';

const notificationIdsShown = new Set();
const INK = '#14161B';
const INK_SOFT = '#6B7078';
const LINE = '#E7E8EC';
const CANVAS = '#FAFAFA';
const CARD = '#FFFFFF';
// const ACCENT = '#4640DE';
const TODAY_RING = '#4640DE';
const CONFIRMED = '#3B6FE0';
const REMOVED = '#9A9EA6';
const withAlpha = (hex, alpha) => `${hex}${alpha}`;
const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
// const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MAX_VISIBLE = 2;
const LANE_HEIGHT = 20; // px height of each continuous-event bar row
const MAX_VISIBLE_LANES = 2; // max multi-day bar rows shown per week before "+N more all-day"
const OVERFLOW_STRIP_BG = '#FFFFFF'; // background of the "+N more all-day" strip
function buildMonthDays(anchor) {
  const first = anchor.clone().startOf('month');
  const dow = (first.day() + 6) % 7; // 0 = Monday
  const gridStart = first.clone().subtract(dow, 'days');
  return Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, 'days'));
}
function StatusBadge({ meetingOn, size }) {
  const removed = meetingOn === 'removed';
  const color = removed ? REMOVED : CONFIRMED;
  const Icon = removed ? Ban : Check;
  return (
    <span style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      boxShadow: '0 0 0 1.5px rgba(255,255,255,0.9)',
    }}
    >
      <Icon size={Math.round(size * 0.62)} color="#fff" strokeWidth={3.2} />
    </span>
  );
}
StatusBadge.propTypes = {
  meetingOn: PropTypes.string.isRequired,
  size: PropTypes.number,
};
StatusBadge.defaultProps = {
  size: 15,
};
const eventShape = PropTypes.shape({
  id: PropTypes.string,
  title: PropTypes.string,
  meetingOn: PropTypes.string,
  date: PropTypes.arrayOf(PropTypes.string),
  time: PropTypes.arrayOf(PropTypes.string),
});
function EventChip({
  evt, onClick, onEnter, onLeave, stripe,
}) {
  const removed = evt.meetingOn === 'removed';
  const stripeColor = removed ? REMOVED : CONFIRMED;
  return (
    <button
      type="button"
      onClick={() => onClick(evt)}
      onMouseEnter={(e) => onEnter(e, evt)}
      onMouseLeave={onLeave}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
        background: withAlpha(removed ? REMOVED : CONFIRMED, '14'),
        borderRadius: 6,
        borderLeft: stripe ? `3px solid ${stripeColor}` : 'none',
        padding: stripe ? '3px 5px 3px 7px' : '3px 5px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        border: 'none',
        ...(stripe ? { borderLeft: `3px solid ${stripeColor}` } : {}),
        opacity: removed ? 0.6 : 1,
      }}
    >
      <StatusBadge meetingOn={evt.meetingOn} size={14} />
      <span style={{
        flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0,
      }}
      >
        <span style={{
          fontSize: 9.5, fontWeight: 700, color: removed ? REMOVED : CONFIRMED, flexShrink: 0, whiteSpace: 'nowrap',
        }}
        >
          {evt.time[0]}
        </span>
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 11.5,
          color: INK,
          fontWeight: 500,
          textDecoration: removed ? 'line-through' : 'none',
        }}
        >
          {evt.title}
        </span>
      </span>
    </button>
  );
}
EventChip.propTypes = {
  evt: eventShape.isRequired,
  onClick: PropTypes.func.isRequired,
  onEnter: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  stripe: PropTypes.bool,
};
EventChip.defaultProps = {
  stripe: false,
};
function eventDayRange(evt) {
  const [startDate, endDate] = evt.date;
  return {
    start: moment(startDate, 'MM/DD/YYYY').startOf('day'),
    end: moment(endDate, 'MM/DD/YYYY').startOf('day'),
  };
}
function ContinuousChip({
  evt, onClick, onEnter, onLeave, roundLeft, roundRight, showLabel,
}) {
  const removed = evt.meetingOn === 'removed';
  const color = removed ? REMOVED : CONFIRMED;
  return (
    <button
      type="button"
      onClick={() => onClick(evt)}
      onMouseEnter={(e) => onEnter(e, evt)}
      onMouseLeave={onLeave}
      title={evt.title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        width: '100%',
        height: LANE_HEIGHT - 3,
        boxSizing: 'border-box',
        background: withAlpha(color, '20'),
        borderTopLeftRadius: roundLeft ? 6 : 0,
        borderBottomLeftRadius: roundLeft ? 6 : 0,
        borderTopRightRadius: roundRight ? 6 : 0,
        borderBottomRightRadius: roundRight ? 6 : 0,
        borderLeft: roundLeft ? `3px solid ${color}` : 'none',
        padding: '0 6px',
        cursor: 'pointer',
        border: 'none',
        fontFamily: 'inherit',
        overflow: 'hidden',
        opacity: removed ? 0.6 : 1,
        pointerEvents: 'auto',
      }}
    >
      {showLabel ? (
        <>
          <StatusBadge meetingOn={evt.meetingOn} size={12} />
          <span style={{
            fontSize: 9.5, fontWeight: 700, color, flexShrink: 0, whiteSpace: 'nowrap',
          }}
          >
            {evt.time[0]}
          </span>
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: INK, fontWeight: 500, textDecoration: removed ? 'line-through' : 'none',
          }}
          >
            {evt.title}
          </span>
        </>
      ) : (
        <span style={{
          fontSize: 11, color: INK, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
        >
          {evt.title}
        </span>
      )}
    </button>
  );
}
ContinuousChip.propTypes = {
  evt: eventShape.isRequired,
  onClick: PropTypes.func.isRequired,
  onEnter: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  roundLeft: PropTypes.bool,
  roundRight: PropTypes.bool,
  showLabel: PropTypes.bool,
};
ContinuousChip.defaultProps = {
  roundLeft: false,
  roundRight: false,
  showLabel: false,
};
function HoverTooltip({ info }) {
  if (!info) return null;
  const removed = info.evt.meetingOn === 'removed';
  return (
    <div style={{
      position: 'fixed', left: info.x, top: Math.max(info.y - 10, 70), transform: 'translate(-50%, -100%)', zIndex: 9999, pointerEvents: 'none',
    }}
    >
      <div style={{
        background: CARD, color: INK, borderRadius: 9, padding: '9px 11px', minWidth: 180, maxWidth: 260, border: `1px solid ${LINE}`, boxShadow: '0 14px 32px rgba(20,22,27,0.14)',
      }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5,
        }}
        >
          <StatusBadge meetingOn={info.evt.meetingOn} size={16} />
          <span style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: 0.2, color: INK_SOFT, textTransform: 'uppercase',
          }}
          >
            {removed ? 'Removed' : 'Confirmed'}
          </span>
        </div>
        <div style={{
          fontSize: 12.5, fontWeight: 600, lineHeight: 1.35, marginBottom: 4, color: INK,
        }}
        >
          {info.evt.title}
        </div>
        <div style={{ fontSize: 11, color: INK_SOFT }}>
          {info.evt.time[0]}
          {' '}
          –
          {' '}
          {info.evt.time[1]}
        </div>
      </div>
      <div style={{
        position: 'absolute', left: '50%', bottom: -4, width: 9, height: 9, background: CARD, borderRight: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`, transform: 'translateX(-50%) rotate(45deg)',
      }}
      />
    </div>
  );
}
HoverTooltip.propTypes = {
  info: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    evt: eventShape,
  }),
};
HoverTooltip.defaultProps = {
  info: null,
};
const navBtnStyle = {
  height: 28,
  width: 28,
  borderRadius: 7,
  border: `1px solid ${LINE}`,
  background: CARD,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: INK,
};
function DayOverflowPopover({
  info, onEventEnter, onEventLeave, onPopoverEnter, onPopoverLeave, ViewDetailPage,
}) {
  if (!info) return null;
  const {
    x, y, events,
  } = info;
  return (
    <div
      onMouseEnter={onPopoverEnter}
      onMouseLeave={onPopoverLeave}
      style={{
        position: "fixed",
        left: Math.min(Math.max(x, 140), window.innerWidth - 140),
        top: Math.min(y, window.innerHeight - 40),
        transform: "translate(-50%, 8px)",
        zIndex: 9998,
      }}
    >
      <div style={{
        background: CARD, color: INK, borderRadius: 10,
        width: 250, maxHeight: 280, overflowY: "auto",
        border: `1px solid ${LINE}`,
        boxShadow: "0 16px 36px rgba(20,22,27,0.16)",
        padding: 8,
      }}
      >
        <div style={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, color: INK_SOFT,
          textTransform: "uppercase", padding: "4px 6px 7px",
        }}
        >
          Multi-day meetings
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {events.map((e) => (
            <EventChip
              key={e.id}
              evt={e}
              stripe
              onClick={(evt) => ViewDetailPage(true, evt.id)}
              onEnter={onEventEnter}
              onLeave={onEventLeave}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
DayOverflowPopover.propTypes = {
  info: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    day: PropTypes.instanceOf(Date),
    events: PropTypes.arrayOf(eventShape),
  }),
  onEventEnter: PropTypes.func.isRequired,
  onEventLeave: PropTypes.func.isRequired,
  onPopoverEnter: PropTypes.func.isRequired,
  onPopoverLeave: PropTypes.func.isRequired,
  ViewDetailPage: PropTypes.func.isRequired,
};
DayOverflowPopover.defaultProps = {
  info: null,
};
function Calendars() {
  const dispatch = useDispatch();
  const edit = useSelector((state) => state.edit.edit);
  const localData = useSelector((state) => state.localRedux);
  const ProfileData = localData.profile;
  const profile = ProfileData;
  const storeSubcription = localData.subcription;
  const { apiValues } = useSelector((state) => state.CalendarRData);
  const { emailapiValues, emailactiontype } = useSelector((state) => state.EmailData);
  function extractTimeZoneName(tzString) {
    const parts = tzString.split(" ");
    return parts[1];
  }
  const tzString = profile?.timeZone || "(GMT+0:00) UTC";
  const tz = extractTimeZoneName(tzString);
  const currentrealTime = moment().tz(tz).format("YYYY-MM-DD HH:mm")
  const currentDate = new Date(currentrealTime);
  const [meetingData, setMeetingData] = useState([]);
  const [modelOpen, setModelOpen] = useState(false);
  const [mObject, setMObject] = useState();
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [hoverInfo, setHoverInfo] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [overflowInfo, setOverflowInfo] = useState(null);
  const hideTimeoutRef = useRef(null);
  const [state, setState] = useState({
    value: moment(currentDate),
    selectedValue: moment(currentDate),
  });
  const iconWrapper = {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#407fe9',
    backgroundColor: '#407fe915',
  };
  const OutLookDateTimeForTimeZone = (dateTime) => {
    const DateTime = `${dateTime.replace(/\.\d+/, '')}.000Z`;
    const formatedDateTime = moment(DateTime, 'YYYY-MM-DDTHH:mm:ss.Z').utc().format('YYYY-MM-DDTHH:mm:ss.000000');
    return formatedDateTime;
  };
  const ZohoDateTimeForTimeZone = (dateTime) => {
    const formatedDateTime = moment(dateTime, 'YYYYMMDDTHHmmssZ').utc().format('YYYY-MM-DDTHH:mm:ss.000000');
    return formatedDateTime;
  };
  function convertTimeZoneAndFormat(dateTimeStr, profileTimeZone) {
    const extractTimezoneName = (tzStr) => {
      const match = tzStr.match(/\)\s*([A-Za-z/_]+)/);
      return match ? match[1] : '';
    };
    const toTimezone = extractTimezoneName(profileTimeZone.timeZone);
    const utcMoment = moment.parseZone(dateTimeStr).utc();
    const targetMoment = utcMoment.clone().tz(toTimezone);
    return {
      date: targetMoment.format('MM/DD/YYYY'),
      time: targetMoment.format('h:mm a'),
    };
  }
  function convertTimeZoneAndFormat1(dateTimeStr, profileTimeZone) {
    const extractTimezoneName = (tzStr) => {
      const match = tzStr.match(/\)\s*([A-Za-z/_]+)/);
      return match ? match[1] : '';
    };
    const toTimezone = extractTimezoneName(profileTimeZone.timeZone);
    const utcTime = moment.utc(dateTimeStr);
    const targetMoment = utcTime.clone().tz(toTimezone);
    return {
      date: targetMoment.format('MM/DD/YYYY'),
      time: targetMoment.format('h:mm a'),
    };
  }
  const DetailsOpen = (value, data) => {
    if (storeSubcription?.isActive) {
      setModelOpen(value);
      setMObject(data);
      setOpenPopup(false);
    } else {
      message.info('Your subscription is not active. Please renew to continue');
    }
  };
  function normalizeGoogleEventDate(node, profile, isEnd = false) {
    if (node.dateTime) {
      const { date, time } = convertTimeZoneAndFormat(node.dateTime, profile);
      return { date, time };
    }
    if (node.date) {
      let dateStr = node.date;
      if (isEnd) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const utcDate = new Date(Date.UTC(y, m - 1, d));
        utcDate.setUTCDate(utcDate.getUTCDate() - 1);
        dateStr = utcDate.toISOString().slice(0, 10);
      }
      const syntheticDateTime = isEnd
        ? `${dateStr}T23:59:59`
        : `${dateStr}T00:00:00`;

      const { date, time } = convertTimeZoneAndFormat(syntheticDateTime, profile);
      return { date, time };
    }
    return null;
  }
  const transformMeetingsData = (meetings) => meetings.map((meeting) => {
    const {
      id, summary, subject, start, end, creator, responseStatus, dateandtime, title, uid, organizer, status, isCancelled,
    } = meeting;
    let formattedFromDate;
    let formattedFromTime;
    let formattedToDate;
    let formattedToTime;
    if (emailapiValues?.emailType === 'Google') {
      const fromInfo = normalizeGoogleEventDate(start, profile, false);
      const toInfo = normalizeGoogleEventDate(end, profile, true);
      // const from = convertTimeZoneAndFormat(start.dateTime, profile);
      // const to = convertTimeZoneAndFormat(end.dateTime, profile);
      formattedFromDate = fromInfo.date;
      formattedFromTime = fromInfo.time;
      formattedToDate = toInfo.date;
      formattedToTime = toInfo.time;
    } else if (emailapiValues?.emailType === 'Outlook') {
      const startFrom = OutLookDateTimeForTimeZone(start.dateTime);
      const endTo = OutLookDateTimeForTimeZone(end.dateTime);
      const from = convertTimeZoneAndFormat1(startFrom, profile);
      const to = convertTimeZoneAndFormat1(endTo, profile);
      formattedFromDate = from.date;
      formattedFromTime = from.time;
      formattedToDate = to.date;
      formattedToTime = to.time;
    } else if (emailapiValues?.emailType === 'Zoho') {
      const startFrom = ZohoDateTimeForTimeZone(dateandtime?.start);
      const endTo = ZohoDateTimeForTimeZone(dateandtime?.end);
      const from = convertTimeZoneAndFormat1(startFrom, profile);
      const to = convertTimeZoneAndFormat1(endTo, profile);
      formattedFromDate = from.date;
      formattedFromTime = from.time;
      formattedToDate = to.date;
      formattedToTime = to.time;
    }
    return {
      title: emailapiValues?.emailType === 'Google'
        ? (summary && summary !== '' ? summary : 'No Subject')
        : emailapiValues?.emailType === 'Outlook'
          ? (subject && subject !== '' ? subject : 'No Subject')
          : emailapiValues?.emailType === 'Zoho'
            ? (title && title !== '' ? title : 'No Subject')
            : (summary && summary !== '' ? summary : 'No Subject'),
      date: [formattedFromDate, formattedToDate],
      id: emailapiValues?.emailType === 'Zoho' ? uid : id,
      meetingOn: emailapiValues?.emailType === 'Google' ? status
        : emailapiValues?.emailType === 'Outlook' ? (isCancelled ? 'removed' : 'confirmed')
          : emailapiValues?.emailType === 'Zoho' ? 'confirmed' : 'confirmed',
      creator: emailapiValues?.emailType === 'Google' ? organizer
        : emailapiValues?.emailType === 'Outlook' ? responseStatus
          : emailapiValues?.emailType === 'Zoho' ? organizer
            : creator,
      time: [formattedFromTime, formattedToTime],
    };
  });
  async function GetAll(StartDate, EndDate) {
    setLoading(true);
    try {
      const response = await FetchAllRecentMeetings(StartDate, EndDate);
      if (response.data.success === false) {
        dispatch(caldrSuccess('NoCall'));
      } else {
        let FilterMeetings = [];
        if (emailapiValues?.emailType === 'Zoho') {
          FilterMeetings = response.data.events.filter((item) => item.rsvp !== 'no');
        }
        if (emailapiValues?.emailType === 'Google') {
          FilterMeetings = response.data.events.filter((meeting) => {
            if (meeting.status === 'cancelled') return false;
            if (meeting.organizer?.self) return true;
            if (meeting.creator?.self) return true;
            const selfAttendee = meeting.attendees?.find((att) => att.self);
            return selfAttendee && selfAttendee.responseStatus !== 'declined';
          });
        }
        if (emailapiValues?.emailType === 'Outlook') {
          FilterMeetings = response.data.events.filter((item) => item.responseStatus.response !== 'declined');
        }
        setMeetingData(transformMeetingsData(FilterMeetings));
        dispatch(caldrSuccess('NoCall'));
      }
    } catch (error) {
      message.error('Email connection error. Update your settings to restore access');
    } finally {
      setLoading(false);
    }
  }
  const goToMonth = (nextValue) => {
    if (storeSubcription?.isActive) {
      setState({ value: nextValue, selectedValue: nextValue });
      dispatch(caldrSuccess('ApiCall'));
    } else {
      message.info('Your subscription is not active. Please renew to continue');
    }
  };
  const step = (dir) => goToMonth(state.value.clone().add(dir, 'month'));
  const getListData = useCallback((value) => meetingData.filter((event) => {
    const { start, end } = eventDayRange(event);
    // Multi-day events are rendered separately as a single continuous bar
    // (see multiDayEvents / weekSegments below), so exclude them here to
    // avoid the same meeting being duplicated as a chip on every day it spans.
    if (!start.isSame(end, 'day')) return false;
    return value.isBetween(start, end, 'days', '[]');
  }), [meetingData]);
  const multiDayEvents = useMemo(
    () => meetingData.filter((event) => {
      const { start, end } = eventDayRange(event);
      return !start.isSame(end, 'day');
    }),
    [meetingData],
  );
  const buildWeekSegments = useCallback((weekDays, events) => {
    const weekStart = weekDays[0];
    const weekEnd = weekDays[6];
    const segments = events
      .map((evt) => {
        const { start, end } = eventDayRange(evt);
        if (end.isBefore(weekStart, 'day') || start.isAfter(weekEnd, 'day')) return null;
        const clipStart = moment.max(start, weekStart);
        const clipEnd = moment.min(end, weekEnd);
        return {
          evt,
          colStart: clipStart.diff(weekStart, 'days'),
          colEnd: clipEnd.diff(weekStart, 'days'),
          isStart: start.isSame(clipStart, 'day'),
          isEnd: end.isSame(clipEnd, 'day'),
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.colStart - b.colStart) || ((b.colEnd - b.colStart) - (a.colEnd - a.colStart)));
    const lanes = [];
    segments.forEach((seg) => {
      let laneIdx = lanes.findIndex((lane) => lane.every((s) => s.colEnd < seg.colStart || s.colStart > seg.colEnd));
      if (laneIdx === -1) {
        laneIdx = lanes.length;
        lanes.push([]);
      }
      lanes[laneIdx].push(seg);
      seg.lane = laneIdx;
    });
    return { segments, laneCount: lanes.length };
  }, []);
  const matchesQuery = useCallback((e) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (e.title || '').toLowerCase().includes(q);
  }, [query]);
  const filteredByQuery = useCallback((list) => list.filter(matchesQuery), [matchesQuery]);
  const handleEnter = (e, evt) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHoverInfo({ x: r.left + r.width / 2, y: r.top, evt });
  };
  const handleLeave = () => setHoverInfo(null);
  const days = useMemo(() => buildMonthDays(state.value), [state.value]);
  const weeks = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < days.length; i += 7) chunks.push(days.slice(i, i + 7));
    return chunks;
  }, [days]);
  const visibleMultiDayEvents = useMemo(
    () => multiDayEvents.filter(matchesQuery),
    [multiDayEvents, matchesQuery],
  );
  const weekSegments = useMemo(
    () => weeks.map((weekDays) => buildWeekSegments(weekDays, visibleMultiDayEvents)),
    [weeks, visibleMultiDayEvents, buildWeekSegments],
  );
  const currentMonth = state.value.month();
  const today = useMemo(() => moment(), []);
  const headerLabel = state.value.format('MMMM YYYY');
  useEffect(() => {
    if (emailapiValues !== '' && !emailactiontype) {
      if (apiValues === 'ApiCall') {
        let startOfMonthStr;
        let endOfMonthStr;
        if (emailapiValues?.emailType === 'Google' || emailapiValues?.emailType === 'Outlook') {
          startOfMonthStr = moment.tz(state.value, tz).startOf('month').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
          endOfMonthStr = moment.tz(state.value, tz).endOf('month').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
        } else if (emailapiValues?.emailType === 'Zoho') {
          startOfMonthStr = moment.tz(state.value, tz).startOf('month').utc().format('YYYYMMDD');
          endOfMonthStr = moment.tz(state.value, tz).endOf('month').utc().format('YYYYMMDD');
        }
        if (startOfMonthStr && endOfMonthStr) {
          GetAll(startOfMonthStr, endOfMonthStr)
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailapiValues, emailactiontype, apiValues]); // state
  useEffect(() => {
    const socket = getSocket();
    const handleNotification = (data) => {
      const events = [data];
      let FilterMeetings = [];
      if (emailapiValues?.emailType === 'Zoho') {
        FilterMeetings = events.filter((item) => item.rsvp !== 'no');
      }
      if (emailapiValues?.emailType === 'Google') {
        FilterMeetings = events.filter((meeting) => {
          if (meeting.creator?.self) {
            return true;
          }
          const selfAttendee = meeting.attendees?.find((att) => att.self);
          return selfAttendee && selfAttendee.responseStatus !== 'declined';
        });
      }
      if (emailapiValues?.emailType === 'Outlook') {
        FilterMeetings = events.filter((item) => item.responseStatus.response !== 'declined');
      }
      const newdata = transformMeetingsData(FilterMeetings);
      if (FilterMeetings.length > 0) {
        setMeetingData((prev) => [...prev, ...newdata]);
      }
      dispatch(caldrSuccess('NoCall'));
      if (!notificationIdsShown.has(newdata[0].id)) {
        notificationIdsShown.add(newdata[0].id);
        const contentsd = (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={iconWrapper}><CalendarIcon /></div>
            <div style={{ lineHeight: 1.4, cursor: 'pointer' }}>
              <div style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
                You have received a new meeting
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  DetailsOpen(true, newdata[0].id);
                  NotifcationUI.destroy();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    DetailsOpen(true, newdata[0].id);
                  }
                }}
                className="splTxt"
                style={{ marginTop: 2 }}
              >
                {newdata[0].title}
              </div>
            </div>
          </div>
        );
        NotifcationUI.open({
          message: null,
          description: contentsd,
          placement: 'bottomRight',
          duration: 5,
          style: {
            borderRadius: 10,
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            padding: '10px 14px',
            width: 360,
          },
        });
      }
    };
    socket.off('meeting', handleNotification);
    socket.on('meeting', handleNotification);
    const handleConnect = () => {
      socket.emit('join', profile.userId);
    };
    socket.off('connect', handleConnect);
    socket.on('connect', handleConnect);
    return () => {
      socket.off('meeting', handleNotification);
      socket.off('connect', handleConnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);
  const scheduleHideOverflow = useCallback(() => {
    cancelHide();
    hideTimeoutRef.current = setTimeout(() => setOverflowInfo(null), 150);
  }, [cancelHide]);
  const handlePopoverEnter = useCallback(() => {
    cancelHide();
  }, [cancelHide]);
  const handlePopoverLeave = useCallback(() => {
    scheduleHideOverflow();
  }, [scheduleHideOverflow]);
  const handleOverflowEnter = useCallback((e, day, dayEvents) => {
    cancelHide();
    const r = e.currentTarget.getBoundingClientRect();
    setOverflowInfo({
      x: r.left + r.width / 2,
      y: r.bottom,
      day: day.toDate(),
      events: dayEvents,
    });
  }, [cancelHide]);
  return (
    <>
      <div className="notification" title="Calender">
        <span className="head-example" role="button" tabIndex={0} onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setOpenPopup(true)
          }
        }} style={{ cursor: (storeSubcription?.isActive && !edit) ? 'pointer' : 'not-allowed' }} onClick={() => setOpenPopup(true)} >
          <CalendarIcon height={24} width={20} />
        </span>
      </div>
      <Popup modelOpen={modelOpen} setModelOpen={setModelOpen} Meeting={mObject} emailCongifDetail={emailapiValues} page="Calendar" />
      <Modal
        open={openPopup}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width="80vw"
        centered
        destroyOnClose
        styles={{
          body: {
            padding: "20px 0px 0px 0px",
            // height: "70vh",
          },
        }}
      >
        <Scrollbars style={{ width: "100%", height: "72vh" }}>

          <div className="mc-root" style={{
            fontFamily: "'Noto Sans',sans-serif",
            background: CANVAS,
            color: INK,
            borderRadius: 12,
            border: `1px solid ${LINE}`,
            fontVariantNumeric: 'tabular-nums',
            margin: "0 auto",
          }}
          >
            <style>{'.mc-root, .mc-root *, .mc-root *::before, .mc-root *::after { box-sizing: border-box; }'}</style>

            {/* TOOLBAR */}
            <div style={{
              display: 'flex', borderRadius: "12px 12px 0px 0px", alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: `1px solid ${LINE}`, background: CARD, flexWrap: 'wrap',
            }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, minWidth: 190,
              }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 8, background: "#407FE9", display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                >
                  <CalendarDays size={16} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: -0.1 }}>{headerLabel}</div>
                  <div style={{ fontSize: 11, color: INK_SOFT }}>Meeting calendar</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button type="button" onClick={() => step(-1)} style={navBtnStyle}><ChevronLeft size={15} /></button>
                <button type="button" onClick={() => step(1)} style={navBtnStyle}><ChevronRight size={15} /></button>
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${LINE}`, borderRadius: 8, padding: '6px 10px', flex: '1 1 200px', minWidth: 160, background: CANVAS,
              }}
              >
                <Search size={14} color={INK_SOFT} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a meeting…"
                  style={{
                    border: 'none', outline: 'none', background: 'transparent', fontSize: 12.5, width: '100%', color: INK,
                  }}
                />
                {query && (
                  <button type="button" onClick={() => setQuery('')} style={{
                    background: 'none', border: 'none', cursor: 'pointer', display: 'flex',
                  }}
                  >
                    <X size={13} color={INK_SOFT} />
                  </button>
                )}
              </div>

              <div>
                <div style={{ display: "flex", background: CANVAS, border: `1px solid ${LINE}`, borderRadius: 8, padding: "10px", gap: 2, cursor: "pointer", }}
                  role="button" tabIndex={0}
                  onClick={() => setOpenPopup(false)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpenPopup(false) }}
                >
                  <X size={14} color={INK_SOFT} />
                </div>
              </div>
              {loading && (
                <span style={{
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: INK_SOFT,
                }}
                >
                  <Loader2 size={13} className="mc-spin" />
                  {' '}
                  Syncing…
                </span>
              )}
            </div>

            {emailactiontype ? (
              <div style={{
                padding: '8px 18px', color: '#DD5B35', fontSize: 12, fontWeight: 600,
              }}
              >
                * No Configuration found!
              </div>
            ) : emailapiValues?.emailType === 'Other Email' && (
              <div style={{
                padding: '8px 18px', color: '#DD5B35', fontSize: 12, fontWeight: 600,
              }}
              >
                * Calendar invites are not supported by your email provider!
              </div>
            )}

            {/* MONTH GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
              {DAY_LABELS.map((d) => (
                <div key={d} style={{
                  padding: '8px 10px', fontSize: 10.5, fontWeight: 700, color: INK_SOFT, letterSpacing: 0.4, borderBottom: `1px solid ${LINE}`,
                }}
                >
                  {d}
                </div>
              ))}
            </div>
            {weeks.map((weekDays, wi) => {
              const { segments, laneCount } = weekSegments[wi];
              const visibleLaneCount = Math.min(laneCount, MAX_VISIBLE_LANES);
              const barsHeight = visibleLaneCount * LANE_HEIGHT;
              const visibleSegments = segments.filter((seg) => seg.lane < MAX_VISIBLE_LANES);
              const hiddenSegments = segments.filter((seg) => seg.lane >= MAX_VISIBLE_LANES);
              return (
                <div key={weekDays[0].format('YYYYMMDD')}>
                  {/* MULTI-DAY STRIP - separate banner row above this week's day cells */}
                  {barsHeight > 0 && (
                    <div style={{
                      position: 'relative',
                      height: barsHeight,
                      padding: '3px 0',
                      marginBottom: hiddenSegments.length > 0 ? 0 : 3,
                      background: CANVAS,
                      borderRight: `1px solid ${LINE}`,
                      // borderBottom: hiddenSegments.length > 0 ? 'none' : `1px solid ${LINE}`,
                    }}
                    >
                      {visibleSegments.map((seg) => (
                        <div
                          key={`${seg.evt.id}-${wi}-${seg.lane}`}
                          style={{
                            position: 'absolute',
                            top: 3 + seg.lane * LANE_HEIGHT,
                            left: `${(seg.colStart / 7) * 100}%`,
                            width: `${((seg.colEnd - seg.colStart + 1) / 7) * 100}%`,
                            padding: '0 2px',
                            boxSizing: 'border-box',
                          }}
                        >
                          <ContinuousChip
                            evt={seg.evt}
                            onClick={(evt) => DetailsOpen(true, evt.id)}
                            onEnter={handleEnter}
                            onLeave={handleLeave}
                            roundLeft={seg.isStart}
                            roundRight={seg.isEnd}
                            showLabel={seg.isStart}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {/* OVERFLOW STRIP - "+N more all-day" when a week has more multi-day events than visible lanes */}
                  {hiddenSegments.length > 0 && (
                    <button
                      type="button"
                      onMouseEnter={(e) => handleOverflowEnter(
                        e,
                        weekDays[0],
                        hiddenSegments.map((seg) => seg.evt),
                      )}
                      onMouseLeave={scheduleHideOverflow}
                      onClick={(e) => handleOverflowEnter(
                        e,
                        weekDays[0],
                        hiddenSegments.map((seg) => seg.evt),
                      )}
                      style={{
                        display: 'block',
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '0px 6px',
                        marginBottom: 3,
                        marginTop: 3,
                        background: OVERFLOW_STRIP_BG,
                        border: '1px dashed #EDEFF5',
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: INK_SOFT,
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {`+${hiddenSegments.length} more all-day`}
                    </button>
                  )
                  }
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                    {weekDays.map((day) => {
                      const inMonth = day.month() === currentMonth;
                      const dayEvents = filteredByQuery(getListData(day));
                      const isToday = day.isSame(today, 'day');
                      const key = day.format('YYYYMMDD');
                      return (
                        <div key={key} style={{
                          minHeight: 80,
                          minWidth: 0,
                          padding: '6px 6px 8px',
                          borderRight: `1px solid ${LINE}`,
                          borderBottom: `1px solid ${LINE}`,
                          borderTop: `1px solid ${LINE}`,
                          background: inMonth ? CARD : CANVAS,
                          overflow: 'hidden',
                        }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 5 }}>
                            <span style={{
                              fontSize: 11.5,
                              fontWeight: isToday ? 800 : 600,
                              color: inMonth ? INK : '#C7C9CF',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              background: isToday ? TODAY_RING : 'transparent',
                              ...(isToday ? { color: '#fff' } : {}),
                            }}
                            >
                              {day.date()}
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {dayEvents.slice(0, MAX_VISIBLE).map((e) => (
                              <EventChip
                                key={e.id + key}
                                evt={e}
                                onClick={(evt) => DetailsOpen(true, evt.id)}
                                onEnter={handleEnter}
                                onLeave={handleLeave}
                              />
                            ))}
                            {dayEvents.length > MAX_VISIBLE && (
                              <button
                                type="button"
                                onMouseEnter={(e) => handleOverflowEnter(e, day, dayEvents)}
                                onMouseLeave={scheduleHideOverflow}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: 10.5,
                                  color: CONFIRMED,
                                  paddingLeft: 4,
                                  fontWeight: 700,
                                  textAlign: 'left',
                                  fontFamily: 'inherit',
                                }}
                              >
                                +
                                {dayEvents.length - MAX_VISIBLE}
                                {' '}
                                more
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <HoverTooltip info={hoverInfo} />
            <DayOverflowPopover
              info={overflowInfo} ViewDetailPage={DetailsOpen}
              onEventEnter={handleEnter}
              onEventLeave={handleLeave}
              onPopoverEnter={handlePopoverEnter}
              onPopoverLeave={handlePopoverLeave}
            />
            <style>{`
              @keyframes mc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
              .mc-spin { animation: mc-spin 1s linear infinite; }
            `}
            </style>
          </div>
        </Scrollbars>
      </Modal >
    </>
  );
}

export default Calendars;